import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { catchError, map, shareReplay } from "rxjs/operators";

export interface JsonLocationResult {
  address: {
    freeformAddress: string;
    streetNumber?: string;
    streetName?: string;
    postalName?: string;
    localName?: string;
    municipality?: string;
    countrySubdivision?: string;
    postalCode?: string;
    countryCode?: string;
  };
  position: {
    lat: number;
    lon: number;
  };
  entityType?: string;
}

interface ZipCityRecord {
  c?: string;
  s?: string;
  la?: number;
  lo?: number;
  z?: string | number;
  /** City population used to rank autocomplete matches (largest first). */
  p?: number;
}

@Injectable({
  providedIn: "root",
})
export class JsonLocationSearchService {
  private readonly JSON_URL = "https://proddevops.blob.core.windows.net/pubweb/json/location/formatted_us_zips_minified.json";
  private readonly MAX_RESULTS = 8;
  private citiesCache$: Observable<ZipCityRecord[]> | null = null;

  constructor(private http: HttpClient) {}

  private getCities(): Observable<ZipCityRecord[]> {
    if (!this.citiesCache$) {
      this.citiesCache$ = this.http.get<ZipCityRecord[]>(this.JSON_URL).pipe(shareReplay(1));
    }
    return this.citiesCache$;
  }

  /**
   * Resolve the geographic center (centroid) of a 5-digit ZIP code from the bundled US ZIP dataset.
   * Returns null when the ZIP isn't found or the dataset can't be loaded so callers can degrade
   * gracefully (e.g. still save the ZIP without coordinates).
   */
  getZipCenter(zip: string): Observable<{ lat: number; lon: number; city?: string; state?: string } | null> {
    const normalized = String(zip || "").trim();
    if (!/^\d{5}$/.test(normalized)) {
      return of(null);
    }

    return this.getCities().pipe(
      map((cities: ZipCityRecord[]) => {
        const match = (cities || []).find(c => c && String(c.z) === normalized);
        if (!match) {
          return null;
        }
        return {
          lat: match.la,
          lon: match.lo,
          city: match.c,
          state: match.s,
        };
      }),
      catchError(() => of(null)),
    );
  }

  searchLocations(term: string): Observable<JsonLocationResult[]> {
    const lowerTerm = (term || "").toLowerCase().trim();
    if (!lowerTerm) {
      return of([]);
    }

    return this.getCities().pipe(
      map((cities: ZipCityRecord[]) => {
        const seen = new Set<string>();
        const matches = (cities || [])
          .filter(city => {
            if (!city?.c) return false;
            const cityState = `${city.c}, ${city.s}`.toLowerCase();
            return (city.z && city.z.toString().includes(lowerTerm)) || cityState.includes(lowerTerm);
          })
          .filter(city => {
            const key = `${city.c!.toLowerCase()}|${city.s!.toLowerCase()}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });

        // Only re-rank when the CDN dataset includes population (`p`). If the
        // older blob (no `p`) is still live, preserve original file order.
        const hasPopulation = matches.some(city => (city.p || 0) > 0);
        if (hasPopulation) {
          matches.sort((a, b) => {
            const popA = a.p || 0;
            const popB = b.p || 0;
            if (popB !== popA) {
              return popB - popA;
            }
            const nameA = `${a.c}, ${a.s}`.toLowerCase();
            const nameB = `${b.c}, ${b.s}`.toLowerCase();
            return nameA.localeCompare(nameB);
          });
        }

        return matches.slice(0, this.MAX_RESULTS).map(
          city =>
            ({
              address: {
                freeformAddress: `${city.c}, ${city.s}`,
                streetNumber: "",
                streetName: "",
                postalName: city.c,
                localName: city.c,
                municipality: city.c,
                countrySubdivision: city.s,
                postalCode: "",
                countryCode: "US",
              },
              position: {
                lat: city.la,
                lon: city.lo,
              },
              entityType: "City",
            } as JsonLocationResult),
        );
      }),
    );
  }
}
